import {makeAutoObservable} from 'mobx';
import PeerConnectionService from 'services/PeerConnectionService';
import {Stream} from 'models/Stream';
import {ConferenceUser} from 'models/ConferenceUser';
import {ChatStore} from './ChatStore';
import WebRTCWebSockets, {VideoType} from 'services/WebRTCWebSockets';
import {Connection} from 'models/Connection';
import {UserStore} from './UserStore';
import ClassApiManager, {
  IClassApiManager,
} from 'services/API/ClassApi/ClassApiManager';
import {LoaderStore} from './LoaderStore';
import {PopUpStore} from './PopUpStore';
import strings from 'translations';

const TIMEOUT_FOR_REACTION = 4000;
const RAISE_HAND_SYMBOL = '✋';

enum ConnectingState {
  inProgress,
  empty,
}

export class ConferenceStore {
  private classApiManager: IClassApiManager = new ClassApiManager();
  private userStore: UserStore;
  private loaderStore: LoaderStore;
  private popUpStore: PopUpStore;

  private sessionId: string | undefined;
  private connectionId: string | undefined;
  private webSocket: WebRTCWebSockets | undefined;
  private webSocketSharing: WebRTCWebSockets | undefined;
  private token: string | undefined;
  private connectingState = ConnectingState.inProgress;

  private reactionTimeout: {[key: string]: NodeJS.Timeout | undefined} = {};

  private connections: Connection[] = [];

  chatStore: ChatStore;

  onDisconnectCallback: (() => void) | undefined;

  constructor(
    userStore: UserStore,
    loaderStore: LoaderStore,
    popUpStore: PopUpStore,
  ) {
    this.userStore = userStore;
    this.loaderStore = loaderStore;
    this.popUpStore = popUpStore;
    this.chatStore = new ChatStore(userStore);
    makeAutoObservable(this);
  }

  get myStream(): Stream | undefined {
    if (this.connections.length === 0) {
      return;
    }

    return this.connections[0].stream;
  }

  get streams(): ConferenceUser[] {
    return this.connections
      .filter(item => !!item.stream && !item.isSharing)
      .map(item => ({
        stream: item.stream!,
        username: item.username,
        connectionId: item.id,
        streamId: item.streamId,
        reaction: item.reaction,
        isLocal: item.isLocal,
        isSharing: item.isSharing ?? false,
      }));
  }

  get shareStream(): ConferenceUser | undefined {
    const connection = this.connections.find(item => item.isSharing);
    if (!connection || !connection?.stream) {
      return;
    }

    return {
      stream: connection.stream!,
      username: connection.username,
      connectionId: connection.id,
      streamId: connection.streamId,
      reaction: connection.reaction,
      isLocal: connection.isLocal,
      isSharing: true,
    };
  }

  get isSharingActive(): boolean {
    const shareConnection = this.connections.find(item => item.isSharing);
    return !!shareConnection;
  }

  setLocalStream = (stream: Stream) => {
    if (this.connections.length > 0) {
      this.connections[0] = {...this.connections[0], stream};
    }
  };

  setRemoteStream = (
    stream: Stream,
    username: string,
    connectionId: string,
  ) => {
    const remoteConnection = this.connections.find(
      item => item.id == connectionId,
    );

    if (remoteConnection) {
      remoteConnection.stream = stream;
      remoteConnection.username = username;
    }
  };

  stopShareScreen = async () => {
    const shareConnectionIndex = this.connections.findIndex(
      item => item.isSharing,
    );

    if (shareConnectionIndex == -1) return;

    this.connections[shareConnectionIndex].stream
      ?.getTracks()
      .forEach(track => {
        track.stop();
      });
    this.connections[shareConnectionIndex].peerConnection.disconnect();
    this.webSocketSharing?.disconnect();
    this.webSocketSharing = undefined;
    this.connections.splice(shareConnectionIndex, 1);
  };

  shareScreen = async () => {
    if (!this.sessionId) {
      return;
    }

    if (!!this.shareStream) {
      const error =
        strings.conferenceScreen.severalScreensCannotBeShownSimultaneously;
      this.popUpStore.setError(error);
      throw Error(error);
    }

    await this.runConference(this.sessionId, VideoType.screen);
  };

  setReaction = (fromConnectionId: string, reaction?: string) => {
    this.connections.forEach(item => {
      if (item.id == fromConnectionId) {
        item.reaction = reaction;
      }
    });
  };

  sendReaction = (reaction: string) => {
    if (this.connections.length > 0 && this.sessionId) {
      this.classApiManager.sendReaction(this.sessionId, reaction);
      this.webSocket?.sendReaction(reaction);
    }
  };

  sendRaiseHand = () => {
    if (this.connections.length > 0 && this.sessionId) {
      this.classApiManager.sendReaction(this.sessionId, RAISE_HAND_SYMBOL);
      this.webSocket?.sendRaiseHand();
    }
  };

  muteAll = () => {
    this.webSocket?.sendMuteAll();
  };

  switchAudio = () => {
    if (this.connections.length == 0) {
      return;
    }

    const connection = this.connections[0];
    const stream = connection.stream?.switchAudio();
    if (!stream) {
      return;
    }

    this.webSocket?.sendStreamPropertyChanged(
      connection.streamId,
      stream.isAudioEnabled,
      false,
    );

    this.setLocalStream(stream);
  };

  switchVideo = () => {
    if (this.connections.length == 0) {
      return;
    }

    const connection = this.connections[0];
    const stream = connection.stream?.switchVideo();
    if (!stream) {
      return;
    }

    this.webSocket?.sendStreamPropertyChanged(
      connection.streamId,
      stream.isVideoEnabled,
      true,
    );
    this.setLocalStream(stream);
  };

  updateRemoteStream = (
    streamId: string,
    property: string,
    active: boolean,
  ) => {
    const connections = [...this.connections];
    const remote = connections.find(item => item.streamId == streamId);
    if (remote) {
      if (property == 'audioActive') {
        remote.stream = remote.stream?.audioEnableChangeValue(active);
      } else {
        remote.stream = remote.stream?.videoEnableChangeValue(active);
      }
    }
    this.connections = connections;
  };

  disconnect = () => {
    this.connections.forEach(connection => {
      connection.stream?.getTracks().forEach(track => {
        track.stop();
      });
      connection.peerConnection.disconnect();
    });

    this.webSocket?.disconnect();
    this.webSocket = undefined;
    this.chatStore.disconnect();

    this.sessionId = undefined;
    this.connectionId = undefined;
    this.token = undefined;
    this.connections = [];

    this.webSocketSharing?.disconnect();
    this.webSocketSharing = undefined;
  };

  runConference = async (
    sessionId: string,
    type: VideoType = VideoType.camera,
  ) => {
    this.sessionId = sessionId;
    await this.setUpLocalPeerConnection(type);
  };

  private setUpLocalPeerConnection = async (type: VideoType) => {
    const localPeerConnection = new PeerConnectionService(
      true,
      this.connectionId!,
    );
    let localStream: Stream | undefined;

    localPeerConnection.onIceCandidateAdded = iceCandidate => {
      this.connectionId &&
        this.webSocket?.sendIceCandidate(this.connectionId, iceCandidate);
    };

    localPeerConnection.onLocalStreamAdded = stream => {
      localStream = stream;
    };

    localPeerConnection.onConnectionConnected = async () => {
      console.log('connected local...');
      this.connectingState = ConnectingState.empty;
      this.handlePendingStreams();
    };

    try {
      switch (type) {
        case VideoType.camera:
          await localPeerConnection.runLocalMedia();
          break;

        case VideoType.screen:
          await localPeerConnection.runShareScreen();
          break;

        default:
          break;
      }
    } catch (err) {
      throw err;
    }

    await this.runSession();
    if (!this.connectionId) {
      return;
    }

    const username =
      this.userStore.fullUserName + (type == VideoType.screen ? '_SCREEN' : '');

    this.connections.push({
      peerConnection: localPeerConnection,
      id: this.connectionId ?? '',
      streamId: this.sessionId ?? '',
      isAnswered: false,
      isSharing: type == VideoType.screen,
      username,
      videoActive: true,
      audioActive: type == VideoType.screen,
      isLocal: true,
      stream: localStream,
      type,
    });

    this.runWebSocketConnection(this.connectionId, username, type);
  };

  private publishVideo = async (
    connectionId: string,
    webSocket: WebRTCWebSockets,
    videoType: VideoType,
  ) => {
    const offer = await this.connections
      .find(item => item.id == connectionId)
      ?.peerConnection.createOffer();

    if (!offer) {
      return;
    }

    console.log('offer');
    webSocket.sendPublishVideo(offer.sdp, videoType);
  };

  private runWebSocketConnection = (
    connectionId: string,
    username: string,
    videoType: VideoType,
  ) => {
    if (!this.sessionId) {
      return;
    }

    const webSocket = new WebRTCWebSockets(
      this.sessionId,
      this.userStore.user?.id!,
      this.userStore.user!,
      username,
    );

    switch (videoType) {
      case VideoType.camera:
        this.webSocket = webSocket;
        this.chatStore.configure(webSocket);
        break;

      case VideoType.screen:
        this.webSocketSharing = webSocket;
        break;

      default:
        break;
    }

    webSocket.onOpen = async () => {
      if (this.token && this.sessionId) {
        webSocket.sendJoinRoom(this.token, this.sessionId, videoType);
      }

      this.publishVideo(connectionId, webSocket, videoType);

      setTimeout(() => {
        this.loaderStore.stopLoading();
      }, 1000);
    };

    webSocket.onIceCandidate = (iceCandidate, senderConnectionId) => {
      if (this.connections.length > 0) {
        const connection = this.connections.find(
          item => item.id === senderConnectionId,
        );

        connection?.peerConnection.addIceCandidate(iceCandidate);
      }
    };

    webSocket.onSdpAnswerReceived = sdpAnswer => {
      const notAnsweredConnection = this.connections.find(
        connection => !connection.isAnswered,
      );

      if (!notAnsweredConnection) {
        return;
      }

      notAnsweredConnection.peerConnection.setAnswer(sdpAnswer);
      notAnsweredConnection.isAnswered = true;
    };

    webSocket.onPublishVideo = streamId => {
      if (this.connections.length > 0) {
        const connection = this.connections.find(
          item => item.id == connectionId,
        );

        if (connection) {
          connection.streamId = streamId;
        }
      }
    };

    if (videoType === VideoType.screen) {
      return;
    }

    webSocket.onStreamPropertyChanged = this.updateRemoteStream;

    webSocket.onMuteAll = (fromConnectionId: string) => {
      if (
        this.connectionId == fromConnectionId ||
        this.connections.length == 0
      ) {
        return;
      }

      const connection = this.connections[0];
      if (connection.stream?.isAudioEnabled) {
        this.switchAudio();
      }
    };

    webSocket.onRaiseHandReceived = (
      fromConnectionId: string,
      data: string,
    ) => {
      if (this.reactionTimeout[fromConnectionId]) {
        clearTimeout(this.reactionTimeout[fromConnectionId]);
        this.reactionTimeout[fromConnectionId] = undefined;
      }

      const connection = this.connections.find(
        item => item.id == fromConnectionId,
      );

      this.setReaction(
        fromConnectionId,
        connection?.reaction == RAISE_HAND_SYMBOL
          ? undefined
          : RAISE_HAND_SYMBOL,
      );
    };

    webSocket.onReactionReceived = (fromConnectionId: string, data: string) => {
      if (this.reactionTimeout[fromConnectionId]) {
        clearTimeout(this.reactionTimeout[fromConnectionId]);
        this.reactionTimeout[fromConnectionId] = undefined;
      }

      const parsedData = JSON.parse(data);
      this.setReaction(fromConnectionId, parsedData.reaction);
      this.reactionTimeout[fromConnectionId] = setTimeout(() => {
        this.setReaction(fromConnectionId, undefined);
      }, TIMEOUT_FOR_REACTION);
    };

    webSocket.onParticipantJoined = (id, name, type) => {
      if (type == VideoType.screen && this.isSharingActive) {
        return;
      }

      this.connections.push({
        peerConnection: new PeerConnectionService(false, id),
        id,
        streamId: '',
        isAnswered: false,
        username: name,
        videoActive: true,
        audioActive: type != VideoType.screen,
        isLocal: false,
        isSharing: type == VideoType.screen,
        type,
      });
    };

    webSocket.onParticipantPublished = (
      participantConnectionId,
      streamId,
      type,
    ) => {
      const connection = this.connections.find(item => {
        return item.id == participantConnectionId;
      });

      if (connection) {
        connection.streamId = streamId;
      }

      this.connectingState = ConnectingState.empty;
      this.handlePendingStreams();
    };

    webSocket.onJoinRoom = models => {
      models.forEach(item => {
        this.connections.push({
          peerConnection: new PeerConnectionService(false, item.id),
          streamId: item.streamId,
          id: item.id,
          isAnswered: false,
          username: item.username,
          videoActive: item.videoActive,
          audioActive: item.audioActive,
          isLocal: false,
          isSharing: item.type == VideoType.screen,
          type: item.type,
        });
      });
    };

    webSocket.onParticipantDisconnected = participantConnectionId => {
      this.connections = this.connections.filter(item => {
        if (item.id === participantConnectionId) {
          item.peerConnection.disconnect();
        }

        return item.id != participantConnectionId;
      });
    };
  };

  private handlePendingStreams = () => {
    const notAnsweredConnection = this.connections.find(
      connection => !connection.isAnswered,
    );

    if (!notAnsweredConnection) {
      return;
    }

    if (this.connectingState === ConnectingState.inProgress) {
      return;
    }

    this.connectingState = ConnectingState.inProgress;

    this.setPeerRemoteConnection(notAnsweredConnection);
  };

  private setPeerRemoteConnection = async (remoteConnection: Connection) => {
    remoteConnection.peerConnection.onRemoteStreamAdded = stream => {
      if (!remoteConnection.audioActive) {
        stream.switchAudio();
      }

      if (!remoteConnection.videoActive) {
        stream.switchVideo();
      }

      this.setRemoteStream(
        stream,
        remoteConnection.username,
        remoteConnection.id,
      );
    };

    remoteConnection.peerConnection.onConnectionConnected = async () => {
      console.log('connected...');
      this.connectingState = ConnectingState.empty;
      this.handlePendingStreams();
    };

    remoteConnection.peerConnection.onConnectionFailed = async () => {
      remoteConnection.isAnswered = false;

      if (remoteConnection.isLocal) {
        if (remoteConnection.type === VideoType.screen) {
          console.log('unpublish');
          this.webSocketSharing?.sendUnpublishVideo();
          this.publishVideo(
            remoteConnection.id,
            this.webSocketSharing!,
            remoteConnection.type,
          );
          return;
        }

        this.onDisconnectCallback?.();
        this.popUpStore.setError(
          strings.conferenceScreen.unexpectedProblemTryToReconnect,
        );
        return;
      } else {
        this.webSocket?.sendUnsubscribeFromVideo(remoteConnection.id);
      }

      remoteConnection.stream = undefined;
      remoteConnection.peerConnection.disconnect();
      remoteConnection.peerConnection = new PeerConnectionService(
        remoteConnection.isLocal,
        remoteConnection.id,
      );
      this.handlePendingStreams();
    };

    const offer = await remoteConnection.peerConnection.createOffer();

    this.webSocket?.sendReceiveVideoFrom(offer.sdp, remoteConnection.streamId);
  };

  private runSession = async () => {
    this.loaderStore.startLoading();

    if (!this.sessionId) {
      throw Error();
    }

    try {
      const data = await this.classApiManager.startConference(this.sessionId);
      this.connectionId = data.connectionId;
      this.token = data.token;
      return data;
    } catch (err) {
      throw err;
    }
  };
}
